import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import { TrendingUp, TrendingDown, Calculator } from 'lucide-react';

type TrendType = 'uptrend' | 'downtrend';

interface FibLevel {
  level: string;
  ratio: number;
  price: number;
  color: string;
}

export const FibonacciRetracement = () => {
  const [trendType, setTrendType] = useState<TrendType>('uptrend');
  const [highPrice, setHighPrice] = useState<string>('');
  const [lowPrice, setLowPrice] = useState<string>('');
  const [fibLevels, setFibLevels] = useState<FibLevel[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);

  const fibRatios = [
    { level: '0%', ratio: 0, color: '#ef4444' },
    { level: '23.6%', ratio: 0.236, color: '#f97316' },
    { level: '38.2%', ratio: 0.382, color: '#eab308' },
    { level: '50%', ratio: 0.5, color: '#22c55e' },
    { level: '61.8%', ratio: 0.618, color: '#3b82f6' },
    { level: '78.6%', ratio: 0.786, color: '#8b5cf6' },
    { level: '100%', ratio: 1, color: '#ec4899' },
  ];

  const calculateFibonacci = () => {
    const high = parseFloat(highPrice);
    const low = parseFloat(lowPrice);

    if (isNaN(high) || isNaN(low)) {
      return;
    }

    const difference = high - low;
    const levels: FibLevel[] = [];
    const data: any[] = [];

    if (trendType === 'uptrend') {
      // Uptrend: Calculate retracement from high
      // Formula: High - (Difference × Ratio)
      fibRatios.forEach((fib) => {
        const price = high - (difference * fib.ratio);
        levels.push({
          level: fib.level,
          ratio: fib.ratio,
          price: Math.round(price * 100) / 100,
          color: fib.color,
        });
        data.push({
          level: fib.level,
          price: Math.round(price * 100) / 100,
        });
      });
    } else {
      // Downtrend: Calculate retracement from low
      // Formula: Low + (Difference × Ratio)
      fibRatios.forEach((fib) => {
        const price = low + (difference * fib.ratio);
        levels.push({
          level: fib.level,
          ratio: fib.ratio,
          price: Math.round(price * 100) / 100,
          color: fib.color,
        });
        data.push({
          level: fib.level,
          price: Math.round(price * 100) / 100,
        });
      });
    }

    setFibLevels(levels);
    setChartData(data);
  };

  const handleCalculate = () => {
    calculateFibonacci();
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calculator className="h-5 w-5" />
          Fibonacci Retracement Calculator
        </CardTitle>
        <CardDescription>
          Calculate Fibonacci retracement levels for technical analysis
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Trend Selection */}
        <div className="space-y-2">
          <Label>Trend Direction</Label>
          <Select value={trendType} onValueChange={(value) => setTrendType(value as TrendType)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="uptrend">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-green-500" />
                  Uptrend (Retracement from High)
                </div>
              </SelectItem>
              <SelectItem value="downtrend">
                <div className="flex items-center gap-2">
                  <TrendingDown className="h-4 w-4 text-red-500" />
                  Downtrend (Retracement from Low)
                </div>
              </SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            {trendType === 'uptrend' 
              ? 'Uptrend: Calculates pullback levels from the high price' 
              : 'Downtrend: Calculates bounce levels from the low price'}
          </p>
        </div>

        {/* Price Inputs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="highPrice">High Price</Label>
            <Input
              id="highPrice"
              type="number"
              step="0.01"
              placeholder="Enter high price"
              value={highPrice}
              onChange={(e) => setHighPrice(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="lowPrice">Low Price</Label>
            <Input
              id="lowPrice"
              type="number"
              step="0.01"
              placeholder="Enter low price"
              value={lowPrice}
              onChange={(e) => setLowPrice(e.target.value)}
            />
          </div>
        </div>

        {/* Calculate Button */}
        <Button onClick={handleCalculate} className="w-full">
          Calculate Fibonacci Levels
        </Button>

        {/* Results */}
        {fibLevels.length > 0 && (
          <div className="space-y-6">
            {/* Chart */}
            <div className="h-[400px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="level" 
                    className="text-xs"
                    label={{ value: 'Fibonacci Level', position: 'insideBottom', offset: -5 }}
                  />
                  <YAxis 
                    className="text-xs"
                    domain={['dataMin - 5', 'dataMax + 5']}
                    label={{ value: 'Price', angle: -90, position: 'insideLeft' }}
                  />
                  <Tooltip 
                    contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                    labelStyle={{ color: 'hsl(var(--foreground))' }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="price" 
                    stroke="hsl(var(--primary))" 
                    strokeWidth={3}
                    dot={{ fill: 'hsl(var(--primary))', r: 6 }}
                    name="Retracement Level"
                  />
                  {fibLevels.map((level, index) => (
                    <ReferenceLine 
                      key={index}
                      y={level.price} 
                      stroke={level.color}
                      strokeDasharray="3 3"
                      label={{ value: level.level, fill: level.color, fontSize: 12 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Levels Table */}
            <div className="rounded-lg border border-border overflow-hidden">
              <table className="w-full">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left py-3 px-4 font-semibold text-sm">Level</th>
                    <th className="text-left py-3 px-4 font-semibold text-sm">Ratio</th>
                    <th className="text-right py-3 px-4 font-semibold text-sm">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {fibLevels.map((level, index) => (
                    <tr 
                      key={index} 
                      className="border-t border-border hover:bg-muted/50 transition-colors"
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full" 
                            style={{ backgroundColor: level.color }}
                          />
                          <span className="font-medium">{level.level}</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-muted-foreground">
                        {level.ratio.toFixed(3)}
                      </td>
                      <td className="py-3 px-4 text-right font-semibold text-lg">
                        {level.price.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Interpretation */}
            <div className="rounded-lg bg-muted/50 p-4 space-y-2">
              <h4 className="font-semibold flex items-center gap-2">
                {trendType === 'uptrend' ? (
                  <TrendingUp className="h-4 w-4 text-green-500" />
                ) : (
                  <TrendingDown className="h-4 w-4 text-red-500" />
                )}
                Interpretation
              </h4>
              <p className="text-sm text-muted-foreground">
                {trendType === 'uptrend' 
                  ? 'In an uptrend, these levels represent potential support zones where the price might bounce after a pullback. The 38.2% and 61.8% levels are typically the strongest retracement levels.'
                  : 'In a downtrend, these levels represent potential resistance zones where the price might face selling pressure during a bounce. The 38.2% and 61.8% levels are typically the strongest retracement levels.'}
              </p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
